'use strict';

const BaseStore = require('fluxible/addons/BaseStore');
const FinancialAmount = require('bunsen/models/FinancialAmount');
const Price = require('bunsen/models/inventory/Price');
const User = require('../../../global/models/User');
const numAddCommas = require('dibs-num-add-commas');
const tokenReplace = require('dibs-token-replace');
const currencyUtil = require('dibs-currency-helpers/util');
const intelligentFormat = require('dibs-currency-helpers/intelligentFormat');
const mapValues = require('lodash.mapvalues');
const enabler = require('../../../global/utils/enabler');

class ContactUIStore extends BaseStore {
    constructor(dispatcher) {
        super(dispatcher);
        this.state = {
            modalId: '',
            isOpened: false,
        };
        this.reset();
    }

    openModal({ modalId }) {
        Object.assign(this.state, {
            modalId,
            isOpened: true,
        });
        this.emitChange();
    }

    closeModal() {
        Object.assign(this.state, {
            modalId: '',
            isOpened: false,
            isServerError: false,
        });
        this.emitChange();
    }

    startLoading({ sendToOmtOnSuccess, itemPk }) {
        this.reset();
        Object.assign(this.state, {
            sendToOmtOnSuccess,
            isItem: !!itemPk,
        });
    }

    startSending() {
        this.state.isSending = true;
        this.emitChange();
    }

    doneSending() {
        this.state.formSubmitted = true;
    }

    serverError() {
        Object.assign(this.state, {
            isServerError: true,
            isOpened: true,
            isSending: false,
        });
        this.emitChange();
    }

    showOptIn() {
        this.state.isShowOptIn = true;
        this.emitChange();
    }

    doneLoading(payload) {
        // initialize message before updating ui
        this.dispatcher.waitFor('ContactMessageStore', () => {
            const { item, seller, user, copyText } = payload;
            if (user && this.state.user) {
                // we have the up-to-date profile already
                user.set('profile', this.state.user.get('profile'));
            }
            Object.assign(this.state, {
                copyText,
                seller,
                user,
                isLoading: false,
                isUserInitiallyLoggedIn: !!user,
                isUserNameSet:
                    user &&
                    user.get('profile') &&
                    user.get('profile').getDisplayName({ defaultToEmail: false }),
                sellerCompany: seller.get('sellerProfile.company'),
            });
            if (item) {
                const isShowOffer = item.canMakeBuyerOffer();
                this.state.isShowOffer = isShowOffer;
                if (isShowOffer) {
                    const currency = this.dispatcher.getStore('UserStore').getCurrency();
                    const currencySymbol = FinancialAmount.getCurrencySymbol(currency);
                    const delimiter = currency === FinancialAmount.CURRENCY_EUR ? '.' : ',';

                    // Values are numbers, prices are string representations of corresponding numbers
                    const values = [Price.TYPE_RETAIL, Price.TYPE_NET, Price.TYPE_SALE].map(
                        type => {
                            // getPriceIn defaults to main price if given type's price is not defined
                            return item.getPriceByType(type)
                                ? item.getPriceIn(type, currency)
                                : undefined;
                        }
                    );
                    const [retailValue, netValue, saleValue] = values;
                    const [retailPrice, netPrice, salePrice] = values.map(
                        value => (value ? numAddCommas(value, false, delimiter) : undefined)
                    );

                    let displayPrice;
                    let priceValue;
                    let priceType;
                    const showNetPrice =
                        netValue &&
                        item.isFurniture() &&
                        User.canRequestNetPrice() &&
                        enabler.isEnabled('enable_net_price');
                    if (saleValue) {
                        displayPrice = salePrice;
                        priceValue = saleValue;
                        priceType = Price.TYPE_SALE;
                    } else if (showNetPrice) {
                        displayPrice = netPrice;
                        priceValue = netValue;
                        priceType = Price.TYPE_NET;
                    } else {
                        displayPrice = retailPrice;
                        priceValue = retailValue;
                        priceType = Price.TYPE_RETAIL;
                    }

                    Object.assign(
                        this.state,
                        {
                            isPricePerPiece: item.canMultipleQuantityBePurchased(),
                            pieces: item.getNumberOfPieces(),
                            currency,
                            currencySymbol,
                            displayPrice,
                            priceValue,
                            priceType,
                        },
                        {
                            formattedRetailPrice: intelligentFormat(retailValue, currency),
                            formattedNetPrice: showNetPrice
                                ? intelligentFormat(netValue, currency)
                                : undefined,
                            formattedSalePrice: saleValue
                                ? intelligentFormat(saleValue, currency)
                                : undefined,
                        }
                    );
                }
            }
            this._setBaseModalHeader();
            this.emitChange();
        });
    }

    doneLoadingUserToUser(payload) {
        const { user, copyText, header } = payload;

        Object.assign(this.state, {
            copyText,
            user,
            modalHeader: header,
            isLoading: false,
            isUserInitiallyLoggedIn: !!user,
            isUserNameSet:
                user &&
                user.get('profile') &&
                user.get('profile').getDisplayName({ defaultToEmail: false }),
        });

        this.emitChange();
    }

    toggleOffer() {
        this._toggleField('isOfferEnabled');
        this.clearErrors();
    }

    toggleMessage() {
        this._toggleField('isMessageEnabled');
        this.clearErrors();
    }

    showLowBall() {
        Object.assign(this.state, {
            isShowLowBallWarning: true,
            modalHeader: this.state.copyText.get('offerLowballTitle'),
        });
        this.emitChange();
    }

    hideLowBall() {
        this.state.isShowLowBallWarning = false;
        this._setBaseModalHeader();
        this.emitChange();
    }

    hideMakeOffer() {
        this.state.isShowOffer = false;
        this.emitChange();
    }

    initLowballOptions({ offerAmount }) {
        const { priceValue, currency, isPricePerPiece, copyText } = this.state;
        const fivePercent = 0.05;
        const tenPercent = 0.1;
        const priceFivePercentOff = currencyUtil.round(priceValue * (1 - fivePercent), 0);
        const priceTenPercentOff = currencyUtil.round(priceValue * (1 - tenPercent), 0);
        const perItem = isPricePerPiece ? copyText.get(' per item') : '';

        // Value is what gets set on the offer model and label is visually displayed
        this.state.lowballOptions = [
            {
                value: `${priceFivePercentOff}`,
                label: intelligentFormat(priceFivePercentOff, currency) + perItem,
                percentOff: fivePercent * 100,
                labelSubtext: tokenReplace(copyText.get('lowballOptionAmountOff'), {
                    amountOff: intelligentFormat(priceValue - priceFivePercentOff, currency),
                    percentOff: fivePercent * 100,
                }),
            },
            {
                value: `${priceTenPercentOff}`,
                label: intelligentFormat(priceTenPercentOff, currency) + perItem,
                percentOff: tenPercent * 100,
                labelSubtext: tokenReplace(copyText.get('lowballOptionAmountOff'), {
                    amountOff: intelligentFormat(priceValue - priceTenPercentOff, currency),
                    percentOff: tenPercent * 100,
                }),
            },
        ];

        if (offerAmount) {
            const offerValue = currencyUtil.parseFloat(offerAmount);
            const amountOffValue = priceValue - offerValue;
            const percentOff = amountOffValue / priceValue * 100;
            this.state.lowballOptions.push({
                value: offerAmount,
                label: `${intelligentFormat(offerValue, currency) + perItem} ${copyText.get(
                    'originalOffer'
                )}`,
                percentOff: percentOff,
                labelSubtext: tokenReplace(copyText.get('lowballOptionAmountOff'), {
                    amountOff: intelligentFormat(amountOffValue, currency),
                    percentOff: currencyUtil.format(percentOff, 0),
                }),
                isOriginalOffer: true,
            });
        }

        this.state.lowballOptions.push({
            isOther: true,
            value: 'other',
            label: copyText.get('lowballOtherOptionLabel'),
        });

        // No Lowball option is selected at first so disable submit.
        this.state.submitDisabled = true;
    }

    setLowballOption(payload) {
        const { selectedLowballOption } = payload;
        const prevLowballOption = this.state.selectedLowballOption;
        selectedLowballOption.checked = true;
        if (prevLowballOption) {
            prevLowballOption.checked = false;
            if (prevLowballOption.isOther) {
                // if no longer "other" option, reset offer label subtext for it
                this.setLowballOfferDiscount({ value: '' });
            }
        }
        this.state.selectedLowballOption = selectedLowballOption;
        // Lowball option definitely selected now, enable submit button
        // if it's not the "other" option which is an input
        this.state.submitDisabled = Boolean(selectedLowballOption.isOther);
        this.clearErrors();
    }

    setLowballOfferDiscount(payload) {
        const { selectedLowballOption, currency, priceValue, copyText } = this.state;
        if (!selectedLowballOption || (selectedLowballOption && !selectedLowballOption.isOther)) {
            return;
        }
        if (payload.value === '') {
            selectedLowballOption.labelSubtext = undefined;
            this.state.submitDisabled = true;
            return;
        }
        const offerValue = currencyUtil.parseFloat(payload.value);
        const amountOffValue = priceValue - offerValue;
        if (amountOffValue <= 0) {
            selectedLowballOption.percentOff = undefined;
            selectedLowballOption.labelSubtext = undefined;
        } else {
            const percentOff = amountOffValue / priceValue * 100;
            selectedLowballOption.percentOff = percentOff;
            selectedLowballOption.labelSubtext = tokenReplace(
                copyText.get('lowballOptionAmountOff'),
                {
                    amountOff: intelligentFormat(amountOffValue, currency),
                    percentOff: currencyUtil.format(percentOff, 0),
                }
            );
        }
        this.state.submitDisabled = false;
        // Reset errors after each change
        this.clearErrors();
    }

    startAuth() {
        this.state.isOpened = false;
        this.emitChange();
    }

    doneAuth({ user }) {
        Object.assign(this.state, {
            isOpened: true,
            user,
        });
        this.emitChange();
    }

    clearErrors() {
        this.state.errors = {};
        this.emitChange();
    }

    validateForm(payload) {
        this.clearErrors();
        this.state.errors = mapValues(payload.errors, err => err.message);
        this.emitChange();
    }

    setChatEvents({ chatSuccessEvent, chatAbandonEvent }) {
        Object.assign(this.state, { chatSuccessEvent, chatAbandonEvent });
    }

    setOptimizely(payload) {
        Object.assign(this.state, payload);
        this.emitChange();
    }

    updateModalHeader(headerCopy) {
        Object.assign(this.state, { modalheader: headerCopy });
        this.emitChange();
    }

    reset() {
        Object.assign(this.state, {
            isLoading: true,
            isSending: false,
            isSendComplete: false,
            isServerError: false,
            isShowLowBallWarning: false,
            lowballOptions: [],
            selectedLowballOption: undefined,
            offerSent: false,
            isMessageEnabled: false,
            isOfferEnabled: false,
            submitDisabled: false,
            modalHeader: '',
            sellerCompany: '',
            errors: {},
            formSubmitted: false,
            chatSuccessEvent: null,
            chatAbandonEvent: null,
        });
        this.emitChange();
    }

    _toggleField(field) {
        this.state[field] = !this.state[field];
        this.emitChange();
    }

    _setBaseModalHeader() {
        this.state.modalHeader = tokenReplace(this.state.copyText.get('header'), {
            dealer: this.state.sellerCompany,
        });
    }
}

ContactUIStore.storeName = 'ContactUIStore';
ContactUIStore.handlers = {
    'contact:modal:open': 'openModal',
    'contact:modal:close': 'closeModal',
    'contact:loading:start': 'startLoading',
    'contact:loading:done': 'doneLoading',
    'contact:sending:start': 'startSending',
    'contact:sending:done': 'doneSending',
    'contact:userToUser:loading:done': 'doneLoadingUserToUser',
    'contact:serverError': 'serverError',
    'contact:showOptIn': 'showOptIn',
    'contact:offer:toggle': 'toggleOffer',
    'contact:message:toggle': 'toggleMessage',
    'contact:form:lowBall:show': 'showLowBall',
    'contact:form:lowBall:hide': 'hideLowBall',
    'contact:form:makeOffer:hide': 'hideMakeOffer',
    'contact:form:lowball:options:init': 'initLowballOptions',
    'contact:form:lowball:options:set': 'setLowballOption',
    'contact:form:lowball:options:offer': 'setLowballOfferDiscount',
    'contact:auth:start': 'startAuth',
    'contact:auth:done': 'doneAuth',
    'contact:form:validated': 'validateForm',
    'contact:chat:setEvents': 'setChatEvents',
    'contact:reset': 'reset',
    'contact:optimizely:setOptimizely': 'setOptimizely',
};

module.exports = ContactUIStore;



// WEBPACK FOOTER //
// ./assets/js/contact/shared/stores/ContactUIStore.es.js